import { Injectable } from '@angular/core';
import { Animal } from '../../animal';
import { AnimalEvent } from '../animal-event';
import { Pregnancy } from './pregnancy';
import {
    Filter,
    FilterOp,
    NetworkStatusService,
    SortMode,
    SortOrder
} from 'libs/arch/src/lib/repositories';
import {
    BaseIndexedDbRepository,
    SynchronizationService
} from 'libs/arch/src/lib/repositories/indexed-db';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { map, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from '@homeleite/arch';

@Injectable({ providedIn: 'root' })
export class PregnanciesRepository extends BaseIndexedDbRepository<Pregnancy, Pregnancy> {
    protected override readonly storeName: string = 'pregnancies';

    constructor(
        dbService: NgxIndexedDBService,
        synchronizationService: SynchronizationService,
        private networkStatus: NetworkStatusService,
        private apiService: ApiService
    ) {
        super(dbService, synchronizationService);

        this.registerAnimalReferences('animalId');
    }

    public override insert(pregnancy: Pregnancy): Observable<Pregnancy> {
        return super.insert(pregnancy).pipe(
            tap((savedPregnancy) => {
                const transformedEvent: AnimalEvent = {
                    id: savedPregnancy.id,
                    date: savedPregnancy.date,
                    animal: savedPregnancy.animal,
                    type: 'PREGNANCY',
                    created: savedPregnancy.created
                };

                this.dbService.add('animalEvents', transformedEvent).subscribe({
                    error: (err) => {
                        console.error('Falha ao agregar evento de Prenhez em animalEvents:', err);
                    }
                });
            })
        );
    }

    protected mapToEntity(dbItem: Pregnancy): Pregnancy {
        return Pregnancy.fromJson(dbItem);
    }

    protected override mapToSummary(item: Pregnancy): Pregnancy {
        return item;
    }

    protected override getSearchableFields(): string[] {
        return ['animal.name', 'animal.register', 'date'];
    }

    protected override getResourcePath(): string {
        return 'pregnancies';
    }

    public findByAnimal(animal: Animal): Observable<Pregnancy[]> {
        const animalId: string | undefined = animal?.id;
        if (!animalId) {
            return of([]);
        }

        const getFromDb = () => {
            return this.executeIndexedDbQuery({
                filters: [new Filter('animalId', FilterOp.EQ, animalId)],
                sortOrders: [
                    new SortOrder('date', SortMode.DSC),
                    new SortOrder('created', SortMode.DSC)
                ]
            });
        };

        if (!this.networkStatus.isOnline()) {
            return getFromDb();
        }

        return this.fetchPregnanciesBy(animalId)
            .pipe(catchError(() => getFromDb()));
    }

    private fetchPregnanciesBy(animalId: string): Observable<Pregnancy[]> {
        return this.apiService.root
            .path(this.getResourcePath())
            .path('animal')
            .path(animalId)
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    Array.isArray(response) ? response.map((item) => Pregnancy.fromJson(item)) : []
                ),
                tap((pregnancies) => {

                    if (pregnancies?.length > 0) {
                        const integrationPregnancies = pregnancies.filter((pregnancy) => {
                            const { auditData } = pregnancy;
                            return (
                                auditData?.createdByIntegration || (auditData?.createdByApp && auditData?.modifiedByIntegration)
                            );
                        });

                        if (integrationPregnancies.length > 0) {
                            this.saveLocally(integrationPregnancies).subscribe({
                                error: (err) => console.error(
                                    'Falha ao salvar prenhez em lote:',
                                    err.message || err
                                )
                            });
                        }
                    }
                })
            );
    }
}
